// Turns a run's raw stats dict into the short chip summary shown on the status cards.
// Only counters that actually happened surface here, with human labels and tooltips;
// the raw dict (zeros and all) stays available behind the card's "All stats" toggle.

// Display priority for the known counters. `proposed` is handled specially (the
// replace/remove/add breakdown folds into its text) and always leads; `violation_id`
// is not a counter and always trails as the Targeted chip.
const OUTCOME_DISPLAY = [
    // remediate pass
    { key: 'held', label: 'Held', tooltip: 'Waiting on a pending higher-tier proposal; retried on the next run' },
    { key: 'deferred', label: 'Deferred', tooltip: 'A candidate source errored; retried on the next run' },
    { key: 'skipped_no_sources', label: 'No sources selected', tooltip: 'Every source for the resource type was deselected this run' },
    { key: 'no_proposal', label: 'No suitable candidate', tooltip: 'Sources answered, but no candidate was worth proposing' },
    { key: 'resolved', label: 'Resolved', tooltip: 'The coverage gap had already closed; no proposal needed' },
    { key: 'skipped', label: 'Skipped', tooltip: 'Already covered by an open or dismissed proposal' },
    { key: 'llm', label: 'Model', tooltip: 'The model explicitly selected for this run' },
    // find pass
    { key: 'violations', label: 'Violations found', tooltip: 'Rule violations detected this pass' },
    { key: 'suppressed', label: 'Suppressed', tooltip: 'Already tracked by a pending violation or open proposal' },
    { key: 'created', label: 'New violations', tooltip: 'Violations recorded for the first time this run' },
]

// Keys consumed by the special-cased chips (or redundant with them): `remediated`
// always equals `proposed`, and the kind counters are `proposed`'s breakdown.
const FOLDED_KEYS = ['proposed', 'remediated', 'replace', 'remove', 'add', 'violation_id']

const KNOWN_KEYS = [...FOLDED_KEYS, ...OUTCOME_DISPLAY.map(entry => entry.key)]

const proposalsEntry = (stats) => {
    if (!stats.proposed) return null
    const kinds = ['replace', 'remove', 'add']
        .filter(kind => stats[kind])
        .map(kind => `${stats[kind]} ${kind}`)
    const breakdown = kinds.length ? ` (${kinds.join(', ')})` : ''
    return {
        key: 'proposed',
        text: `Proposals: ${stats.proposed}${breakdown}`,
        tooltip: 'Change proposals created this run, awaiting admin review',
    }
}

export const summarizeRunStats = (stats) => {
    const entries = []

    const proposals = proposalsEntry(stats)
    if (proposals) entries.push(proposals)

    for (const { key, label, tooltip } of OUTCOME_DISPLAY) {
        if (stats[key]) entries.push({ key, text: `${label}: ${stats[key]}`, tooltip })
    }

    // Future counters must never silently vanish from the summary: anything truthy
    // and unrecognized is shown humanized rather than dropped.
    for (const [key, value] of Object.entries(stats)) {
        if (KNOWN_KEYS.includes(key) || !value) continue
        entries.push({
            key,
            text: `${key.replace(/_/g, ' ')}: ${typeof value === 'object' ? JSON.stringify(value) : value}`,
            tooltip: 'Unrecognized counter — see "All stats" for the raw values',
        })
    }

    // Decided before the Targeted chip is appended: a targeted run with zero
    // counters (its violation went non-pending before the thread ran) still needs
    // an outcome statement — "Targeted" alone says nothing about what happened.
    if (entries.length === 0) {
        entries.push({
            key: 'none',
            text: 'No actions taken',
            tooltip: 'Every counter was zero — nothing was pending or actionable this run',
        })
    }

    if ('violation_id' in stats) {
        entries.push({
            key: 'violation_id',
            text: `Targeted: violation #${stats.violation_id}`,
            tooltip: 'This run remediated only the selected violation',
        })
    }

    return entries
}
